/**
 * Client-side validation for calendar notes
 * Matches backend validation rules
 */

/**
 * Validate date format (YYYY-MM-DD)
 * @param {string} date - Date string to validate
 * @returns {object} { valid: boolean, error: string }
 */
export function validateDate(date) {
  if (!date) return { valid: false, error: 'Date is required' };

  const dateRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
  if (!dateRegex.test(date)) {
    return { valid: false, error: 'Date must be in YYYY-MM-DD format' };
  }

  // Check if date is valid (e.g., not Feb 30)
  const parsedDate = new Date(date);
  if (isNaN(parsedDate.getTime())) {
    return { valid: false, error: 'Invalid date' };
  }

  return { valid: true };
}

/**
 * Validate time format (HH:mm, 24-hour)
 * @param {string} time - Time string to validate
 * @returns {object} { valid: boolean, error: string }
 */
export function validateTime(time) {
  if (!time) return { valid: true }; // Time is optional

  const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
  if (!timeRegex.test(time)) {
    return { valid: false, error: 'Time must be in HH:mm format (24-hour)' };
  }

  return { valid: true };
}

/**
 * Validate description
 * @param {string} description - Description text to validate
 * @returns {object} { valid: boolean, error: string }
 */
export function validateDescription(description) {
  if (!description || description.trim().length === 0) {
    return { valid: false, error: 'Description is required' };
  }

  if (description.length > 1000) {
    return { valid: false, error: 'Description cannot exceed 1000 characters' };
  }

  return { valid: true };
}

/**
 * Validate entire calendar note
 * @param {object} noteData - { date, startTime, description, operationId }
 * @returns {object} { valid: boolean, errors: object }
 */
export function validateCalendarNote(noteData) {
  const errors = {};

  const dateCheck = validateDate(noteData.date);
  if (!dateCheck.valid) errors.date = dateCheck.error;

  const timeCheck = validateTime(noteData.startTime);
  if (!timeCheck.valid) errors.startTime = timeCheck.error;

  const descCheck = validateDescription(noteData.description);
  if (!descCheck.valid) errors.description = descCheck.error;

  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
}

/**
 * Validate query filters
 * @param {object} filters - { month, startDate, endDate }
 * @returns {object} { valid: boolean, errors: object }
 */
export function validateFilters(filters) {
  const errors = {};

  if (filters.month) {
    const monthRegex = /^\d{4}-(0[1-9]|1[0-2])$/;
    if (!monthRegex.test(filters.month)) {
      errors.month = 'Month must be in YYYY-MM format';
    }

    // Cannot use both month and date range
    if (filters.startDate || filters.endDate) {
      errors.general = 'Cannot use both month and date range filters';
    }
  }

  if (filters.startDate || filters.endDate) {
    if (!filters.startDate || !filters.endDate) {
      errors.dateRange = 'Both start date and end date are required';
    } else {
      const startCheck = validateDate(filters.startDate);
      const endCheck = validateDate(filters.endDate);

      if (!startCheck.valid) errors.startDate = startCheck.error;
      if (!endCheck.valid) errors.endDate = endCheck.error;

      if (startCheck.valid && endCheck.valid) {
        const start = new Date(filters.startDate);
        const end = new Date(filters.endDate);

        if (end < start) {
          errors.endDate = 'End date must be after start date';
        }

        // Check if range exceeds 1 year
        const oneYear = 365 * 24 * 60 * 60 * 1000;
        if (end - start > oneYear) {
          errors.dateRange = 'Date range cannot exceed 1 year';
        }
      }
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
}

/**
 * Format date to YYYY-MM-DD
 * @param {string|Date} date - Date to format
 * @returns {string|null} Formatted date or null
 */
export function formatDate(date) {
  if (!date) return null;

  if (date instanceof Date) {
    return date.toISOString().split('T')[0];
  }

  // If already in correct format
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  // Try to parse and format
  try {
    const parsed = new Date(date);
    if (isNaN(parsed.getTime())) {
      console.error('Invalid date format:', date);
      return null;
    }
    return parsed.toISOString().split('T')[0];
  } catch (error) {
    console.error('Invalid date format:', date);
    return null;
  }
}

/**
 * Format time to HH:mm (24-hour)
 * @param {string} time - Time to format
 * @returns {string|null} Formatted time or null
 */
export function formatTime(time) {
  if (!time) return null;

  // If already in HH:mm format
  if (typeof time === 'string' && /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/.test(time)) {
    return time;
  }

  // Try to extract HH:mm from longer format
  if (typeof time === 'string') {
    const match = time.match(/(\d{2}):(\d{2})/);
    if (match) {
      const hours = parseInt(match[1]);
      const minutes = parseInt(match[2]);
      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      }
    }
  }

  console.error('Invalid time format:', time);
  return null;
}
